'use strict';
(() => {
  const registryKey = '__pinmeMoonlitStoryMotion';
  if (window[registryKey]) {
    window[registryKey].initialize(document);
    return;
  }

  const instances = new Map();
  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = window.matchMedia('(max-width: 1023px)');
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const constrained = () => preference.matches || Boolean(connection && connection.saveData);
  const framesIn = (scope, selector) => [
    ...(scope.matches && scope.matches(selector) ? [scope] : []),
    ...scope.querySelectorAll(selector),
  ];
  const hasVideo = (frame) => Boolean(frame.dataset.motionDesktop || frame.dataset.motionMobile);
  const listen = (target, type, handler, options) => {
    if (target.addEventListener) {
      target.addEventListener(type, handler, options);
      return () => target.removeEventListener(type, handler, options);
    }
    if (type === 'change' && target.addListener) {
      target.addListener(handler);
      return () => target.removeListener(handler);
    }
    return () => {};
  };
  const watchVisibility = (frame, update) => {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        const entry = entries[entries.length - 1];
        update(Boolean(entry && entry.isIntersecting && entry.intersectionRatio >= 0.35));
      }, { threshold: [0, 0.35] });
      observer.observe(frame);
      return () => observer.disconnect();
    }
    const measure = () => {
      const box = frame.getBoundingClientRect();
      let left = Math.max(0, box.left);
      let top = Math.max(0, box.top);
      let right = Math.min(innerWidth, box.right);
      let bottom = Math.min(innerHeight, box.bottom);
      for (let parent = frame.parentElement; parent; parent = parent.parentElement) {
        const style = getComputedStyle(parent);
        const clip = parent.getBoundingClientRect();
        if (/(auto|scroll|hidden|clip)/.test(style.overflowX)) {
          left = Math.max(left, clip.left);
          right = Math.min(right, clip.right);
        }
        if (/(auto|scroll|hidden|clip)/.test(style.overflowY)) {
          top = Math.max(top, clip.top);
          bottom = Math.min(bottom, clip.bottom);
        }
      }
      const area = Math.max(0, right - left) * Math.max(0, bottom - top);
      update(box.width > 0 && box.height > 0 && area / (box.width * box.height) >= 0.35);
    };
    const removeScroll = listen(document, 'scroll', measure, { capture: true, passive: true });
    const removeResize = listen(window, 'resize', measure, { passive: true });
    measure();
    return () => {
      removeScroll();
      removeResize();
    };
  };

  const initializeVideo = (frame) => {
    if (instances.has(frame) || !hasVideo(frame)) return;
    const video = frame.querySelector('video');
    const loader = frame.querySelector('[data-mn-motion-loader]');
    if (!video) return;
    const disposers = [];
    const status = document.createElement('span');
    status.className = 'mn-editorial-motion-status';
    status.setAttribute('aria-live', 'polite');
    status.hidden = true;
    frame.append(status);
    const originalAttributes = ['tabindex', 'role', 'aria-label'].map(name => [name, frame.getAttribute(name)]);
    let visible = false;
    let destroyed = false;
    let fallbackMode = false;
    let failed = false;
    let loadedSource = '';
    let attempt = 0;
    let pending = false;
    let loadingTimer = null;
    let animation = null;
    const selectedSource = () => mobile.matches
      ? frame.dataset.motionMobile || frame.dataset.motionDesktop
      : frame.dataset.motionDesktop || frame.dataset.motionMobile;
    const selectedFallback = () => mobile.matches
      ? frame.dataset.motionFallbackMobile || frame.dataset.motionFallback || frame.dataset.motionFallbackDesktop
      : frame.dataset.motionFallbackDesktop || frame.dataset.motionFallback || frame.dataset.motionFallbackMobile;
    const wanted = () => !destroyed && visible && !document.hidden && !constrained();
    const render = active => { frame.dataset.motionPlaying = String(active); };
    const retryable = active => {
      frame.dataset.motionRetry = String(Boolean(active));
      status.hidden = !active;
      status.textContent = active ? 'Tap to retry animation' : '';
      if (active) {
        frame.tabIndex = 0;
        frame.setAttribute('role', 'button');
        frame.setAttribute('aria-label', `${frame.dataset.motionLabel || 'Product detail'}. Retry animation`);
      } else {
        originalAttributes.forEach(([name, value]) => value === null ? frame.removeAttribute(name) : frame.setAttribute(name, value));
      }
    };
    const removeAnimation = () => {
      if (animation) {
        animation.onload = null;
        animation.onerror = null;
        animation.removeAttribute('src');
        animation.remove();
      }
      animation = null;
      frame.dataset.motionFallbackPlaying = 'false';
    };
    const setLoading = active => {
      const loading = Boolean(active && wanted());
      frame.dataset.motionLoading = String(loading);
      frame.setAttribute('aria-busy', String(loading));
      if (loader) loader.hidden = !loading;
      if (!loading && loadingTimer !== null) {
        clearTimeout(loadingTimer);
        loadingTimer = null;
      }
      if (loading && loadingTimer === null) {
        loadingTimer = setTimeout(() => {
          loadingTimer = null;
          if (!wanted()) return;
          if (fallbackMode) {
            failed = true;
            stop();
            retryable(true);
          } else {
            useFallback();
          }
        }, fallbackMode ? 20000 : 9000);
      }
    };
    const stopVideo = () => {
      attempt += 1;
      pending = false;
      video.pause();
      render(false);
      setLoading(false);
    };
    const stop = () => {
      stopVideo();
      removeAnimation();
    };
    const clearSource = () => {
      stopVideo();
      loadedSource = '';
      video.removeAttribute('src');
      video.load();
    };
    const startFallback = () => {
      if (!wanted() || failed) return;
      const source = selectedFallback();
      if (!source) {
        failed = true;
        setLoading(false);
        retryable(true);
        return;
      }
      if (animation && animation.getAttribute('src') === source) return;
      removeAnimation();
      const candidate = new Image();
      animation = candidate;
      candidate.className = 'mn-editorial-motion-fallback';
      candidate.alt = '';
      candidate.setAttribute('aria-hidden', 'true');
      candidate.decoding = 'async';
      candidate.hidden = true;
      candidate.onload = () => {
        if (animation !== candidate || !wanted()) return;
        candidate.hidden = false;
        frame.dataset.motionFallbackPlaying = 'true';
        retryable(false);
        setLoading(false);
      };
      candidate.onerror = () => {
        if (animation !== candidate || destroyed) return;
        failed = true;
        removeAnimation();
        setLoading(false);
        retryable(wanted());
      };
      frame.insertBefore(candidate, loader || null);
      setLoading(true);
      candidate.src = source;
    };
    const useFallback = () => {
      fallbackMode = true;
      clearSource();
      startFallback();
    };
    const update = () => {
      if (!wanted()) {
        stop();
        retryable(false);
        return;
      }
      if (failed) {
        retryable(true);
        return;
      }
      if (fallbackMode) {
        startFallback();
        return;
      }
      const source = selectedSource();
      if (!source || !video.canPlayType('video/mp4')) {
        useFallback();
        return;
      }
      if (source !== loadedSource) {
        clearSource();
        loadedSource = source;
        video.src = source;
        video.load();
      }
      if (pending || (!video.paused && !video.ended)) return;
      const version = ++attempt;
      pending = true;
      setLoading(true);
      let result;
      try {
        result = video.play();
      } catch (error) {
        pending = false;
        useFallback();
        return;
      }
      Promise.resolve(result).catch(() => {
        if (destroyed || version !== attempt || !wanted()) return;
        useFallback();
      }).finally(() => {
        if (version === attempt) pending = false;
      });
    };
    const retry = () => {
      if (!wanted() || !failed) return;
      failed = false;
      fallbackMode = false;
      retryable(false);
      clearSource();
      update();
    };
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.controls = false;
    video.preload = 'none';
    video.tabIndex = -1;
    video.setAttribute('webkit-playsinline', '');
    frame.dataset.motionReady = 'true';
    frame.dataset.motionFallbackPlaying = 'false';
    render(false);
    setLoading(false);
    retryable(false);
    disposers.push(listen(video, 'playing', () => {
      if (!wanted() || fallbackMode || loadedSource !== selectedSource()) {
        stopVideo();
        return;
      }
      render(true);
      retryable(false);
      setLoading(false);
    }));
    disposers.push(listen(video, 'pause', () => {
      if (fallbackMode) return;
      if (video.paused || !wanted()) {
        render(false);
        setLoading(false);
        if (wanted() && loadedSource) useFallback();
      }
    }));
    disposers.push(listen(video, 'timeupdate', () => {
      if (wanted() && !fallbackMode && !video.paused && video.readyState >= 2) {
        render(true);
        setLoading(false);
      }
    }));
    ['loadstart', 'waiting', 'stalled'].forEach(event => {
      disposers.push(listen(video, event, () => {
        if (loadedSource && !fallbackMode && (video.readyState < 3 || frame.dataset.motionPlaying !== 'true')) setLoading(true);
      }));
    });
    disposers.push(listen(video, 'error', () => {
      if (!destroyed && loadedSource && !fallbackMode && wanted()) useFallback();
    }));
    disposers.push(listen(video, 'emptied', () => render(false)));
    disposers.push(listen(document, 'visibilitychange', () => {
      if (!document.hidden && failed) retry();
      update();
    }));
    disposers.push(listen(window, 'online', retry));
    disposers.push(listen(frame, 'click', retry));
    disposers.push(listen(frame, 'touchend', retry, { passive: true }));
    disposers.push(listen(frame, 'keydown', event => {
      if (failed && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        retry();
      }
    }));
    disposers.push(listen(mobile, 'change', () => {
      failed = false;
      if (!fallbackMode) clearSource();
      update();
    }));
    disposers.push(listen(preference, 'change', update));
    if (connection && connection.addEventListener) disposers.push(listen(connection, 'change', update));
    disposers.push(watchVisibility(frame, next => {
      if (visible === next) return;
      visible = next;
      if (visible && failed) {
        failed = false;
        fallbackMode = false;
        clearSource();
      }
      update();
    }));
    instances.set(frame, () => {
      destroyed = true;
      disposers.forEach(dispose => dispose());
      stop();
      clearSource();
      retryable(false);
      status.remove();
      ['motionReady', 'motionPlaying', 'motionLoading', 'motionFallbackPlaying', 'motionRetry'].forEach(key => delete frame.dataset[key]);
      frame.removeAttribute('aria-busy');
      if (loader) loader.hidden = true;
    });
  };

  const initializeLegacy = (frame) => {
    if (instances.has(frame) || hasVideo(frame) || !frame.dataset.animationSrc) return;
    const fallback = frame.querySelector('img, span:not([data-mn-motion-loader])');
    const loader = frame.querySelector('[data-mn-motion-loader]');
    const disposers = [];
    let visible = false;
    let animation = null;
    let destroyed = false;
    let loadingTimer = null;
    const setLoading = (active) => {
      frame.dataset.motionLoading = String(active);
      frame.setAttribute('aria-busy', String(active));
      if (loader) loader.hidden = !active;
      if (!active && loadingTimer !== null) {
        clearTimeout(loadingTimer);
        loadingTimer = null;
      }
    };
    const restore = () => {
      if (animation) animation.remove();
      animation = null;
      if (fallback) fallback.hidden = false;
      setLoading(false);
    };
    const update = () => {
      if (destroyed || !visible || document.hidden || constrained()) {
        restore();
        return;
      }
      if (animation) return;
      const candidate = new Image();
      animation = candidate;
      candidate.className = 'mn-editorial-cue-animation';
      candidate.alt = 'CUE character preview';
      candidate.width = Number(frame.dataset.animationWidth) || 1400;
      candidate.height = Number(frame.dataset.animationHeight) || 1400;
      candidate.hidden = true;
      candidate.addEventListener('load', () => {
        if (animation !== candidate || destroyed || !visible || document.hidden || constrained()) return;
        candidate.hidden = false;
        if (fallback) fallback.hidden = true;
        setLoading(false);
      }, { once: true });
      candidate.addEventListener('error', () => {
        if (animation === candidate) restore();
      }, { once: true });
      frame.append(candidate);
      setLoading(true);
      loadingTimer = setTimeout(restore, 12000);
      candidate.src = frame.dataset.animationSrc;
    };
    frame.dataset.motionReady = 'true';
    disposers.push(listen(preference, 'change', update));
    disposers.push(listen(document, 'visibilitychange', update));
    if (connection && connection.addEventListener) disposers.push(listen(connection, 'change', update));
    disposers.push(watchVisibility(frame, (next) => {
      visible = next;
      update();
    }));
    instances.set(frame, () => {
      destroyed = true;
      disposers.forEach((dispose) => dispose());
      restore();
      delete frame.dataset.motionReady;
      delete frame.dataset.motionLoading;
      frame.removeAttribute('aria-busy');
    });
  };

  const initialize = (scope) => {
    framesIn(scope, '[data-mn-motion]').forEach(initializeVideo);
    framesIn(scope, '[data-mn-cue-motion]').forEach(initializeLegacy);
  };
  const cleanup = (scope) => {
    instances.forEach((dispose, frame) => {
      if (frame === scope || scope.contains(frame)) {
        dispose();
        instances.delete(frame);
      }
    });
  };
  window[registryKey] = { initialize };
  initialize(document);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => cleanup(event.target));
})();


